import { useState } from "react";
import type { RoomMember, RoomModAction } from "@gooner/shared";
import { Avatar } from "../../control/ui/Avatar";
import { CrownMark } from "../../control/ui/Crown";
import { cx } from "../../control/ui/primitives";
import { RoomMemberMenu, targetOf } from "./RoomMemberMenu";

/** Faces shown before the pill folds the rest into "+N". */
const SHOWN = 8;

/**
 * Who is watching: ONE pill over the wall's top-left corner — a dark glass capsule with
 * the faces overlapping like a hand of cards, a gold member wearing a small crown, you
 * ringed in the accent, "+N" past eight, and the count beside them. A face is a link to
 * the person's page, opened beside the room; the host's tap on somebody else opens
 * RoomMemberMenu instead (the same menu a chat name opens). Hovering a face names it.
 *
 * A capsule rather than loose discs: a wall is sixteen pictures, and faces dropped bare
 * on top of one read as part of it. The glass gives them a surface of their own, and
 * the overlap keeps a full room to one line of a phone's width.
 *
 * An OVERLAY over the playback, so every pointer event stops here: the tile underneath
 * selects on mouseenter and opens its popup on click, and a face must not read as a
 * press on the clip behind it (see crownHit in RegionFapOverlay for the same fight).
 */
export function RoomRoster({
  watching,
  members,
  you,
  isOwner,
  onMod,
  after,
}: {
  /** Everyone watching — the faces plus the signed-out viewers, who have none. */
  watching: number;
  members: RoomMember[];
  you: string | null;
  isOwner: boolean;
  onMod: (action: RoomModAction, username: string) => void;
  /** Drawn beside the pill, on the same overlay. */
  after?: React.ReactNode;
}) {
  const [menuFor, setMenuFor] = useState<RoomMember | null>(null);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const shown = members.slice(0, SHOWN);
  const hidden = members.length - shown.length;

  return (
    <div
      className="absolute left-2 top-2 z-30 flex max-w-[calc(100%-1rem)] items-center gap-2"
      onMouseEnter={stop}
      onMouseLeave={stop}
      onPointerDown={stop}
      onClick={stop}
    >
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/60 py-1 pl-1.5 pr-3 shadow-lg backdrop-blur">
        <div className="flex items-center">
          {shown.map((m, i) => {
            const mine = m.pid === you;
            const menuable = isOwner && !m.owner && !mine;
            const face = (
              <span
                className={cx(
                  "block rounded-full ring-2 transition-transform hover:z-10 hover:scale-110",
                  mine ? "ring-[var(--color-accent)]" : "ring-black/80",
                )}
              >
                <Avatar name={m.username} src={m.avatarUrl} size="sm" distinct />
              </span>
            );
            return (
              <div key={m.pid} className={cx("relative", i > 0 && "-ml-2")} style={{ zIndex: shown.length - i }}>
                {menuable ? (
                  <button
                    type="button"
                    aria-label={`${m.username} — options`}
                    title={m.username}
                    onClick={() => setMenuFor((cur) => (cur?.pid === m.pid ? null : m))}
                    className="block rounded-full"
                  >
                    {face}
                  </button>
                ) : (
                  <a
                    href={`/u/${encodeURIComponent(m.username)}`}
                    target="_blank"
                    rel="noopener"
                    aria-label={`${m.username}'s profile`}
                    title={m.username}
                    className="block rounded-full"
                  >
                    {face}
                  </a>
                )}
                {/* A gold member wears the crown at the face's corner. The host wears
                    nothing here — the chat names them "(Host)" beside their name. */}
                {m.gold ? (
                  <span
                    aria-hidden
                    title="Gold member"
                    className="absolute -bottom-0.5 -right-0.5 z-20 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#f5c518] ring-2 ring-black/80"
                  >
                    <CrownMark size={8} tone="ink" />
                  </span>
                ) : null}
                {menuFor?.pid === m.pid ? (
                  <RoomMemberMenu target={targetOf(m.username, members)} onClose={() => setMenuFor(null)} onMod={onMod} />
                ) : null}
              </div>
            );
          })}
          {hidden > 0 ? (
            <span className="relative -ml-2 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-semibold text-zinc-200 ring-2 ring-black/80">
              +{hidden}
            </span>
          ) : null}
        </div>
        <span className="whitespace-nowrap text-[11px] font-medium text-zinc-200">
          {watching} watching
        </span>
      </div>
      {after}
    </div>
  );
}
